import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Send, Mic, MicOff, Loader2, ChevronRight, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MillieAvatar } from "./MascotBubble";
import ReportSheet, { ReportFlag } from "./ReportSheet";

const SUGGESTIONS = ["What can I make with mince?", "Add loo paper to the shop", "Swap Tuesday's dinner", "We've run out of milk"];

function sessionFor(hid) {
  const key = `sorted_chat_${hid}`;
  let s = localStorage.getItem(key);
  if (!s) { s = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(key, s); }
  return s;
}

export default function MillieChat() {
  const { activeId, loadHouseholds } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const endRef = useRef(null);
  const sessionId = activeId ? sessionFor(activeId) : null;
  const canSpeak = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    if (!activeId || !sessionId) return;
    api.get(`/millie/chat/${activeId}/${sessionId}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [activeId, sessionId]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (busy || messages.length) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, busy]);

  const send = async (msg) => {
    const m = (msg ?? text).trim();
    if (!m || busy) return;
    setText("");
    setMessages((prev) => [...prev, { id: `u${Date.now()}`, role: "user", content: m }]);
    setBusy(true);
    try {
      const { data } = await api.post("/millie/chat", { household_id: activeId, session_id: sessionId, message: m });
      setMessages((prev) => [...prev, { id: `a${Date.now()}`, role: "assistant", content: data.reply, actions: data.actions, meals: data.meals }]);
      if (data.actions?.length) loadHouseholds();
      const nav = data.actions?.find((a) => a.type === "navigate");
      if (nav) setTimeout(() => navigate(nav.to), 900);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const recordWithWhisper = async () => {
    if (listening) { recRef.current?.stop?.(); return; }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast("Voice isn't available here — type away instead 🎤");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, rec.mimeType.includes("mp4") ? "voice.mp4" : "voice.webm");
          const { data } = await api.post("/millie/transcribe", fd);
          setBusy(false);
          if (data.text) send(data.text); else toast("Didn't catch that — have another go");
        } catch (err) { setBusy(false); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      };
      recRef.current = rec;
      setListening(true);
      rec.start();
      setTimeout(() => rec.state === "recording" && rec.stop(), 15000);
      toast("Listening… tap the mic again when you're done", { id: "rec" });
    } catch { toast.error("Microphone blocked — allow it in your browser settings"); }
  };

  const toggleMic = () => {
    if (!canSpeak) return recordWithWhisper();
    if (listening) { recRef.current?.stop(); return; }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = "en-AU"; rec.interimResults = true; rec.continuous = false;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join(" ");
      setText(t);
      if (e.results[e.results.length - 1].isFinal) { setListening(false); send(t); }
    };
    rec.onerror = () => { setListening(false); toast.error("Didn't catch that — have another go"); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <div className="flex flex-col" data-testid="millie-chat">
      <div className="space-y-3 min-h-[120px]">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2" data-testid="chat-suggestions">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-xs font-bold px-3 py-2 rounded-full">{s}</button>
            ))}
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m) => <Bubble key={m.id} m={m} onGo={(to) => navigate(to)} />)}
        </AnimatePresence>
        {busy && (
          <div className="flex items-center gap-2 text-sorted-slate text-sm" data-testid="chat-thinking">
            <MillieAvatar size={28} float={false} mood="thinking" /> <Loader2 size={14} className="animate-spin" /> Millie's thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-center gap-2 bg-white rounded-full border-2 border-[#FDECEF] shadow-soft pl-4 pr-1.5 py-1.5">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder={listening ? "Listening…" : "Ask Millie anything…"}
          className="flex-1 min-w-0 bg-transparent outline-none font-medium text-sorted-ink py-2" />
        <button onClick={toggleMic} data-testid="chat-mic" aria-label="Speak"
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-sorted-rose text-white animate-pulse" : "bg-muted text-sorted-ink"}`}>
          {listening ? <MicOff size={17} strokeWidth={2.5} /> : <Mic size={17} strokeWidth={2.5} />}
        </button>
        <button onClick={() => send()} disabled={busy || !text.trim()} data-testid="chat-send" aria-label="Send"
          className="w-10 h-10 rounded-full bg-sorted-rose text-white flex items-center justify-center disabled:opacity-40">
          <Send size={16} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

function Bubble({ m, onGo }) {
  const mine = m.role === "user";
  const [report, setReport] = useState(false);
  return (<>
    {!mine && <ReportSheet open={report} onClose={() => setReport(false)} category="millie" title="Report Millie's reply" context={{ label: m.content?.slice(0, 140), message_id: m.id }} />}
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${mine ? "justify-end" : "justify-start"} gap-2`} data-testid={`chat-msg-${m.role}`}>
      {!mine && <MillieAvatar size={28} float={false} mood={m.actions?.length ? "celebrate" : m.meals?.length ? "idea" : "happy"} />}
      <div className={`max-w-[85%] ${mine ? "bg-sorted-rose text-white" : "bg-white text-sorted-ink border-2 border-[#FDECEF]"} rounded-3xl px-4 py-2.5 text-sm font-medium shadow-soft`}>
        <p>{m.content}</p>
        {m.meals?.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {m.meals.map((meal, i) => (
              <div key={i} className="bg-[#FFF6F1] rounded-2xl px-3 py-2" data-testid={`chat-meal-${i}`}>
                <p className="font-bold text-sorted-ink text-sm">{meal.title}</p>
                <p className="text-xs text-sorted-slate">{meal.description}</p>
                {meal.prep_time_min && <p className="text-[11px] font-bold text-sorted-slate mt-0.5 flex items-center gap-1"><Clock size={11} strokeWidth={2.5} />{meal.prep_time_min} min</p>}
              </div>
            ))}
          </div>
        )}
        {m.actions?.filter((a) => a.type !== "navigate").map((a, i) => (
          <button key={i} onClick={() => a.to && onGo(a.to)} data-testid={`chat-action-${a.type}`}
            className="mt-2 w-full text-left bg-sorted-sage/60 text-sorted-ink rounded-2xl px-3 py-2 text-xs font-bold flex items-center gap-1">
            ✓ {a.label} {a.to && <ChevronRight size={13} strokeWidth={2.5} className="ml-auto" />}
          </button>
        ))}
        {!mine && <div className="mt-1.5 -mb-0.5 flex justify-end"><ReportFlag onClick={() => setReport(true)} testId={`chat-report-${m.id}`} /></div>}
      </div>
    </motion.div>
  </>);
}
